(function (window, document) {
  "use strict";

  const UP_CART_STYLE_ID = "hide-upcart-during-checkout";
  const RESTORE_UP_CART_KEY = "restoreUpCartAfterRedirect";

  const safeStorage = {
    get(key, fallback = []) {
      try {
        const value = localStorage.getItem(key);
        return value ? JSON.parse(value) : fallback;
      } catch {
        console.warn("Safari blocked localStorage.getItem");
        return fallback;
      }
    },

    set(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        console.warn("Safari blocked localStorage.setItem");
      }
    },
  };

  function hideUpCartTemporarily() {
    let styleTag = document.getElementById(UP_CART_STYLE_ID);

    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = UP_CART_STYLE_ID;
      styleTag.textContent = `
        #upCart {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(styleTag);
    }

    const upCart = document.querySelector("#upCart");
    if (upCart) {
      upCart.style.setProperty("display", "none", "important");
      upCart.style.setProperty("visibility", "hidden", "important");
      upCart.style.setProperty("opacity", "0", "important");
      upCart.style.setProperty("pointer-events", "none", "important");
    }

    sessionStorage.setItem(RESTORE_UP_CART_KEY, "true");
  }

  function restoreUpCartIfNeeded() {
    const shouldRestore = sessionStorage.getItem(RESTORE_UP_CART_KEY);
    if (!shouldRestore) return;

    const upCart = document.querySelector("#upCart");
    if (upCart) {
      upCart.style.setProperty("display", "block", "important");
      upCart.style.removeProperty("visibility");
      upCart.style.removeProperty("opacity");
      upCart.style.removeProperty("pointer-events");
    }

    document.getElementById(UP_CART_STYLE_ID)?.remove();
    sessionStorage.removeItem(RESTORE_UP_CART_KEY);
  }

  function openPopup(id) {
    const popup = document.getElementById(id);
    if (!popup) return;

    popup.classList.add("active");
    document.body.style.overflow = "hidden";
  }

  function closePopup(id) {
    const popup = document.getElementById(id);
    if (!popup) return;

    popup.classList.remove("active");
    document.body.style.overflow = "";
  }

  function getCurrentPurchaseType(defaultType = "subscription") {
    return (
      document.querySelector(".purchase-type.active")?.dataset.type ||
      defaultType
    );
  }

  function getBundleSectionByPurchaseType(
    type = getCurrentPurchaseType(),
    {
      oneTimeSection = document.querySelector(".one-time-bundle"),
      subscriptionSection = document.querySelector(".subscription-bundle"),
    } = {},
  ) {
    return type === "one-time" ? oneTimeSection : subscriptionSection;
  }

  function getBundleGroupBySet(targetSet, sectionEl) {
    if (!sectionEl || !targetSet) return null;

    return sectionEl.querySelector(
      `.bundle-group[data-bundle-set="${targetSet}"]`,
    );
  }

  function getActiveBundleGroupFromSection(sectionEl) {
    if (!sectionEl) return null;

    const activeOption = sectionEl.querySelector(
      ".bundle-option.active[data-target]",
    );
    const targetSet = activeOption?.dataset.target;

    if (targetSet) {
      const targetGroup = getBundleGroupBySet(targetSet, sectionEl);
      if (targetGroup) return targetGroup;
    }

    return (
      sectionEl.querySelector(".bundle-group.active:not(.hidden)") ||
      sectionEl.querySelector(".bundle-group:not(.hidden)") ||
      sectionEl.querySelector(".bundle-group.active") ||
      null
    );
  }

  function getActiveBundleGroup({
    oneTimeSection = document.querySelector(".one-time-bundle"),
    subscriptionSection = document.querySelector(".subscription-bundle"),
    purchaseType = getCurrentPurchaseType(),
  } = {}) {
    return getActiveBundleGroupFromSection(
      getBundleSectionByPurchaseType(purchaseType, {
        oneTimeSection,
        subscriptionSection,
      }),
    );
  }

  function getBundleSlots(groupEl) {
    if (!groupEl) return [];
    return Array.from(groupEl.querySelectorAll(".bundle-product"));
  }

  function hasSelectedProduct(slot) {
    const variantId = slot?.dataset.id || "";
    const productId = slot?.dataset.product || "";

    return variantId.trim() !== "" && productId.trim() !== "";
  }

  function getSlotNumber(slot) {
    if (!slot) return 1;

    const group = slot.closest(".bundle-group");
    const slots = getBundleSlots(group);
    const index = slots.indexOf(slot);

    return index >= 0 ? index + 1 : 1;
  }

  function getNextEmptySlot(fromSlot = null, fallbackGroup = null) {
    const group = fromSlot ? fromSlot.closest(".bundle-group") : fallbackGroup;
    const slots = getBundleSlots(group);

    if (!slots.length) return null;

    if (!fromSlot) {
      return slots.find((slot) => !hasSelectedProduct(slot)) || null;
    }

    const currentIndex = slots.indexOf(fromSlot);

    for (let i = currentIndex + 1; i < slots.length; i++) {
      if (!hasSelectedProduct(slots[i])) return slots[i];
    }

    return slots.find((slot) => !hasSelectedProduct(slot)) || null;
  }

  function getFilledSlotCount(groupEl) {
    return getBundleSlots(groupEl).filter(hasSelectedProduct).length;
  }

  function getRequiredSlotCount(groupEl) {
    return getBundleSlots(groupEl).length;
  }

  function areAllBundleSlotsFilled(groupEl) {
    const slots = getBundleSlots(groupEl);
    return slots.length > 0 && slots.every(hasSelectedProduct);
  }

  function isBundleComplete(groupEl) {
    return areAllBundleSlotsFilled(groupEl);
  }

  function getFirstIncompleteBundleSlot(groupEl) {
    return (
      getBundleSlots(groupEl).find((slot) => !hasSelectedProduct(slot)) || null
    );
  }

  function collectBundleSelections(groupEl, { useDatasetTitle = true } = {}) {
    if (!groupEl) return [];

    return Array.from(groupEl.querySelectorAll(".bundle-product"))
      .map((slot) => {
        const collectionId = slot.dataset.collection;
        const productId = slot.dataset.product;
        const variantId = slot.dataset.id;
        const title =
          (useDatasetTitle ? slot.dataset.title : "") ||
          slot.querySelector("p")?.innerText?.trim() ||
          "Item";

        if (!collectionId || !productId || !variantId) return null;

        return {
          collectionId: String(collectionId),
          externalProductId: String(productId),
          externalVariantId: String(variantId),
          title,
        };
      })
      .filter(Boolean);
  }

  function groupBundleSelections(rawSelections) {
    const grouped = rawSelections.reduce((acc, item) => {
      const key = `${item.externalProductId}_${item.externalVariantId}`;

      if (!acc[key]) {
        acc[key] = { ...item, quantity: 1 };
      } else {
        acc[key].quantity += 1;
      }

      return acc;
    }, {});

    const selections = [];
    const contents = [];

    Object.values(grouped).forEach((item) => {
      selections.push({
        collectionId: item.collectionId,
        externalProductId: item.externalProductId,
        externalVariantId: item.externalVariantId,
        quantity: item.quantity,
      });

      contents.push(`${item.quantity}x ${item.title}`);
    });

    return { selections, contents };
  }

  async function getRechargeBundleId(bundleData) {
    if (!window.recharge?.bundle?.getBundleId) {
      throw new Error("Recharge bundle SDK is not available.");
    }

    return window.recharge.bundle.getBundleId(bundleData);
  }

  async function buildRechargeBundleLineItem({
    groupEl,
    isSubscription = true,
    extraSelections = [],
    useDatasetTitle = true,
    missingBundleMessage = "Bundle setup is incomplete.",
    emptySelectionsMessage = "Please choose your bundle items first.",
    rechargeMissingMessage = "Recharge bundle is not ready yet.",
  }) {
    if (!groupEl) {
      alert("Please select a bundle first.");
      return null;
    }

    const productId = groupEl.dataset.product;
    const variantId = groupEl.dataset.variant;
    const sellingPlan = isSubscription ? groupEl.dataset.sellingPlan : null;

    if (!productId || !variantId) {
      console.error("Missing bundle parent product/variant on active group");
      alert(missingBundleMessage);
      return null;
    }

    const rawSelections = collectBundleSelections(groupEl, { useDatasetTitle });

    if (!rawSelections.length) {
      alert(emptySelectionsMessage);
      return null;
    }

    const { selections, contents } = groupBundleSelections(rawSelections);

    extraSelections.forEach((item) => {
      const quantity = Number(item.quantity || 1);

      selections.push({
        collectionId: item.collectionId,
        externalProductId: item.externalProductId,
        externalVariantId: item.externalVariantId,
        quantity,
      });

      contents.push(`${quantity}x ${item.title || "Item"}`);
    });

    const bundleData = {
      externalProductId: String(productId),
      externalVariantId: String(variantId),
      selections,
    };

    let rbId;

    try {
      rbId = await getRechargeBundleId(bundleData);
      console.log("✅ Recharge bundle ID:", rbId);
      console.log("✅ Recharge bundle data:", bundleData);
    } catch (err) {
      console.error("❌ Recharge bundle generation failed:", err);
      alert(
        err.message === "Recharge bundle SDK is not available."
          ? rechargeMissingMessage
          : "Error creating bundle. Please try again.",
      );
      return null;
    }

    const parentItem = {
      id: Number(variantId),
      quantity: 1,
      properties: {
        _rb_id: rbId,
        Content: contents.join("\n"),
      },
    };

    if (isSubscription && sellingPlan) {
      parentItem.selling_plan = Number(sellingPlan);
    }

    return {
      parentItem,
      bundleData,
      selections,
      contents,
      rbId,
      parentVariantId: variantId,
      sellingPlan,
    };
  }

  function appendCartItemsToFormData(formData, items) {
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, item.quantity);

      if (item.selling_plan) {
        formData.append(`items[${index}][selling_plan]`, item.selling_plan);
      }

      Object.entries(item.properties || {}).forEach(([key, value]) => {
        formData.append(`items[${index}][properties][${key}]`, value);
      });
    });
  }

  async function addItemsToCart(items, { headers = {} } = {}) {
    const formData = new FormData();
    appendCartItemsToFormData(formData, items);

    return fetch("/cart/add", {
      method: "POST",
      body: formData,
      ...(Object.keys(headers).length ? { headers } : {}),
    });
  }

  window.BundleShared = {
    safeStorage,
    hideUpCartTemporarily,
    restoreUpCartIfNeeded,
    openPopup,
    closePopup,
    getCurrentPurchaseType,
    getBundleSectionByPurchaseType,
    getBundleGroupBySet,
    getActiveBundleGroup,
    getActiveBundleGroupFromSection,
    getBundleSlots,
    getSlotNumber,
    getNextEmptySlot,
    getFilledSlotCount,
    getRequiredSlotCount,
    areAllBundleSlotsFilled,
    isBundleComplete,
    getFirstIncompleteBundleSlot,
    collectBundleSelections,
    groupBundleSelections,
    buildRechargeBundleLineItem,
    appendCartItemsToFormData,
    addItemsToCart,
  };
})(window, document);
